import type { SupervisorRun, SupervisorRunStatus } from '@avisoma/shared';

export type SupervisorIndicator = 'pending' | 'processing' | 'done' | 'error';

export type SupervisorRunPresentation = {
  indicator: SupervisorIndicator;
  label: string;
  /** Queue wait: `enqueuedAt` → `startedAt`. */
  waitMs: number | null;
  /** Run time: `durationMs`, else `startedAt` → `finishedAt`. */
  runMs: number | null;
  /** `wait X · run Y`, or null when neither timing is known. */
  timings: string | null;
  detail: string;
};

const STATUS_LABEL: Record<SupervisorRunStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  done: 'Done',
  error: 'Error',
};

/** `—` when unknown, `0.85s` under a minute, `2m 03s` above. */
export const formatMs = (ms: number | null): string => {
  if (ms === null || !Number.isFinite(ms)) return '—';
  // Round before the minute split so 59.996s reads "1m 00s", never "60.00s" or "0m 60s".
  const total = Math.round(ms / 1000);
  if (total < 60) return `${(ms / 1000).toFixed(2)}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
};

const formatTimings = (waitMs: number | null, runMs: number | null): string | null =>
  waitMs === null && runMs === null ? null : `wait ${formatMs(waitMs)} · run ${formatMs(runMs)}`;

const between = (a?: string, b?: string): number | null => (a && b ? Date.parse(b) - Date.parse(a) : null);

/** Status indicator, label, timings and one-line detail for a `projects.supervisor_run` stamp. */
export const presentSupervisorRun = (run: SupervisorRun | null | undefined): SupervisorRunPresentation => {
  if (!run) {
    return {
      indicator: 'pending',
      label: 'Not run',
      waitMs: null,
      runMs: null,
      timings: null,
      detail: 'The supervisor has not run for this project.',
    };
  }
  const indicator: SupervisorIndicator = run.status === 'running' ? 'processing' : run.status;
  const counts = [
    run.candidates !== undefined ? `${run.candidates} candidates` : null,
    run.judged !== undefined ? `${run.judged} judged` : null,
    run.findings !== undefined ? `${run.findings} findings` : null,
    run.coverage === 'partial' ? 'coverage partial' : null,
  ].filter((s): s is string => s !== null);
  const detail =
    run.status === 'error'
      ? `${run.error ?? 'unknown error'} — finalization still ran.`
      : counts.join(' · ') || STATUS_LABEL[run.status];
  const waitMs = between(run.enqueuedAt, run.startedAt);
  const runMs = run.durationMs ?? between(run.startedAt, run.finishedAt);
  return {
    indicator,
    label: STATUS_LABEL[run.status],
    waitMs,
    runMs,
    timings: formatTimings(waitMs, runMs),
    detail,
  };
};

/**
 * The page view's detail line. A finished run's `findings` counts the whole project,
 * so the page shows its own count instead (plus the partial-coverage flag, since a
 * partial run may have skipped this page); other statuses keep the run detail.
 */
export const pageSupervisorDetail = (run: SupervisorRun | null | undefined, pageFindingCount: number): string => {
  if (run?.status !== 'done') return presentSupervisorRun(run).detail;
  const count =
    pageFindingCount === 0
      ? 'No findings on this page'
      : `${pageFindingCount} finding${pageFindingCount === 1 ? '' : 's'} on this page`;
  return run.coverage === 'partial' ? `${count} · coverage partial` : count;
};
